import { Box, Button, ButtonBase, Hidden, Toolbar, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import ContaBusiness from './contaBusiness';
import NotificacoesAppBar from './notificacoes/notificacoes';
import { useAssinar } from '../assinatura/assine';
import AssinanteSwitch from '../../util/ui/switch/assinanteSwitch';
import { Link, useLocation, matchPath } from 'react-router-dom';
import { useUsuario } from '../../../providers/usuarioProvider';
import Business from '../../../models/business';
import BusinessDb from '../../../db/businessDb';
import { getDoc } from 'firebase/firestore';
import { IconSparkles } from '@tabler/icons-react';
import { Rotas } from './menuRotas';
import logoPreta from '../../../assets/logo_preta.svg';
import logoBranca from '../../../assets/logo_branca.svg';
import { radius } from '../../../theme';

const PREFIX = 'felixToolbar';

const classes = {
  toolbar: `${PREFIX}-toolbar`,
  contaComercial: `${PREFIX}-contaComercial`,
  badge: `${PREFIX}-badge`,
  botaoDocumentacao: `${PREFIX}-botaoDocumentacao`,
  botaoTrocarDeConta: `${PREFIX}-botaoTrocarDeConta`,
};

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  [`&.${classes.toolbar}`]: {
    paddingLeft: theme.spacing(1.5),
    position: 'relative',
  },

  [`& .${classes.contaComercial}`]: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },

  [`& .${classes.badge}`]: { padding: theme.spacing(0) },

  [`& .${classes.botaoDocumentacao}`]: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    textTransform: 'none',
  },

  [`& .${classes.botaoTrocarDeConta}`]: { color: theme.palette.text.secondary },
}));

// Callout de assinatura = estado "warn" do sistema: tint do chip de status,
// hairline no lugar da sombra e pill. Sem gradiente, sem branco puro.
const SubscriptionCallout = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.statusChip.warn.bg,
  color: theme.palette.statusChip.warn.fg,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: radius.pill,
  padding: theme.spacing(0.5, 0.5, 0.5, 2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  maxWidth: 700,
  minWidth: 0,
  height: 'fit-content',
}));

export default function FelixToolbar() {
  const { usuario } = useUsuario();
  const assinar = useAssinar();
  const theme = useTheme();
  const [business, setBusiness] = useState<Business>();
  // A logo preta desaparece sobre o fundo do tema dark.
  const logoFelix = theme.palette.mode === 'dark' ? logoBranca : logoPreta;

  useEffect(() => {
    if (!!usuario?.contaPai || !!usuario?.business) {
      getDoc(
        new BusinessDb().doc({
          businessId: (usuario.contaPai || usuario.business)!,
        }),
      ).then((value) => setBusiness(value.data()));
    }
  }, [usuario?.contaPai, usuario?.business]);

  return (
    <StyledToolbar className={classes.toolbar}>
      <ButtonBase component={Link} to="/painel" focusRipple>
        {!!business ? (
          <img
            alt={`Logo ${business.nome}`}
            src={business.imagemUrl}
            style={{
              maxWidth: '40px',
              height: '40px',
              margin: 'auto',
              objectFit: 'contain',
            }}
          />
        ) : (
          <img
            alt="Logo Felix"
            src={logoFelix}
            style={{
              maxWidth: '40px',
              height: '40px',
              margin: 'auto',
              objectFit: 'contain',
            }}
          />
        )}
      </ButtonBase>
      <Box component="span" mx={2} />
      <Hidden mdDown>
        {(() => {
          const location = useLocation();
          const activeRoute = Rotas.find((rota) => matchPath(rota.pathname, location.pathname));
          if (activeRoute) {
            return (
              <Typography color="textPrimary" variant="h6" display="inline">
                {!!business ? `${business?.nome || 'Escritório'} | ${activeRoute.titulo}` : ''}
              </Typography>
            );
          }
          return null;
        })()}
      </Hidden>
      <Box display="flex" flexGrow={1} />
      <AssinanteSwitch
        renderNaoAssinante={() => {
          if (!!usuario && !!!usuario.contaPai && (usuario.business === usuario.id || !!!usuario.business))
            return (
              <SubscriptionCallout>
                <Box display="flex" flexShrink={0}>
                  <IconSparkles size={16} />
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'inherit',
                    fontWeight: 500,
                    flexGrow: 1,
                    minWidth: 0,
                  }}
                >
                  Assine algum dos nossos{' '}
                  <Typography
                    component="a"
                    variant="body2"
                    href="http://felixnabolsa.com.br/planos"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'inherit',
                      fontWeight: 700,
                      textDecoration: 'underline',
                      '&:hover': {
                        textDecoration: 'none',
                      },
                    }}
                  >
                    planos
                  </Typography>{' '}
                  para desbloquear todas as funcionalidades.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => assinar()}
                  sx={{
                    fontSize: 12,
                    minWidth: 76,
                    height: 28,
                    flexShrink: 0,
                  }}
                >
                  Assinar
                </Button>
              </SubscriptionCallout>
            );
          else return <></>;
        }}
      />
      <Box component="span" m={{ xs: 0, md: 1 }} />
      <NotificacoesAppBar />
      <Box component="span" m={{ xs: 0, md: 1 }} />
      <ContaBusiness />
    </StyledToolbar>
  );
}
